import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import TopBar from "../components/TopBar";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import Toast from "../components/Toast";
import { loadWatermarkDataUrl, applyPdfWatermark } from "../utils/pdfWatermark";
import { todayIST } from "../utils/dateUtils";

// CDN loader helpers
const loadExcelJS = () => new Promise((resolve, reject) => {
  if (window.ExcelJS) { resolve(window.ExcelJS); return; }
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js";
  s.onload = () => resolve(window.ExcelJS);
  s.onerror = reject;
  document.head.appendChild(s);
});

const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf && window.jspdf.jsPDF) { resolve(window.jspdf.jsPDF); return; }
  const s1 = document.createElement("script");
  s1.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
  s1.onload = () => {
    const s2 = document.createElement("script");
    s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
    s2.onload = () => resolve(window.jspdf.jsPDF);
    s2.onerror = reject;
    document.head.appendChild(s2);
  };
  s1.onerror = reject;
  document.head.appendChild(s1);
});

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function pdfHeader(doc, title, subtitle, landscape) {
  const pageW = landscape ? 297 : 210;
  doc.setFillColor(232, 82, 10);
  doc.rect(0, 0, pageW, 18, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12); doc.setFont("helvetica", "bold");
  doc.text("Dolluz Corp EPR  |  " + title, 10, 11);
  doc.setFontSize(8); doc.setFont("helvetica", "normal");
  doc.text(subtitle + "  |  Generated: " + new Date().toLocaleString(), 10, 16);
}

function pdfFooter(doc, landscape) {
  const pageW = landscape ? 297 : 210;
  const pageH = landscape ? 210 : 297;
  const count = doc.getNumberOfPages();
  for (let i = 1; i <= count; i++) {
    doc.setPage(i);
    doc.setTextColor(148, 163, 184); doc.setFontSize(7); doc.setFont("helvetica", "normal");
    doc.text("Page " + i + " of " + count + "  |  Dolluz Corp EPR Portal", pageW / 2, pageH - 4, { align: "center" });
  }
}

function applyXlsHeader(ws, headers) {
  const row = ws.getRow(1);
  row.height = 26;
  headers.forEach((h, i) => {
    const cell = row.getCell(i + 1);
    cell.value = h;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8520A" } };
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = {
      top: { style: "thin", color: { argb: "FFCB4A00" } }, bottom: { style: "thin", color: { argb: "FFCB4A00" } },
      left: { style: "thin", color: { argb: "FFCB4A00" } }, right: { style: "thin", color: { argb: "FFCB4A00" } },
    };
  });
}

function applyXlsDataRow(ws, rowIdx, values, rowBg) {
  const bg = rowBg || "FFFFFFFF";
  const row = ws.getRow(rowIdx);
  row.height = 18;
  values.forEach((val, i) => {
    const cell = row.getCell(i + 1);
    cell.value = val != null ? val : "";
    cell.font = { size: 9, color: { argb: "FF0D1B2A" } };
    cell.alignment = { vertical: "middle" };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } }, bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } }, right: { style: "thin", color: { argb: "FFE5E7EB" } },
    };
  });
}

const Reports = ({ topBarProps, employees, allReviews, clients, cycles }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [toast, setToast] = useState("");
  const [empFilter, setEmpFilter] = useState("All");
  const [clientFilter, setClientFilter] = useState("All");
  const [cmpQ1, setCmpQ1] = useState("");
  const [cmpQ2, setCmpQ2] = useState("");
  const [toastType, setToastType] = useState("");
  const showToast = (msg, type) => { setToast(msg); setToastType(type || ""); setTimeout(() => { setToast(""); setToastType(""); }, 2800); };

  const reviews = allReviews || [];
  const allCycles = (cycles || []).slice().sort((a, b) => {
    if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
    return (a.quarter || 0) - (b.quarter || 0);
  });

  const qData = allCycles.map(c => {
    const cycleReviews = reviews.filter(r => (r.cycle_name || r.quarter || r.q) === c.q);
    const approved = cycleReviews.filter(r => r.status === "Approved");
    const hikeVals = approved.map(r => parseFloat(r.hikeGiven || r.approved_hike || 0)).filter(v => v > 0);
    const avgHike = hikeVals.length ? (hikeVals.reduce((a, b) => a + b, 0) / hikeVals.length).toFixed(1) : 0;
    const sent = c.sent || 0;
    const submitted = c.submitted || 0;
    return { q: c.q, sent, submitted, approved: approved.length, avgHike: parseFloat(avgHike), completion: sent ? Math.round((submitted / sent) * 100) : 0 };
  });

  const allQuarters = qData.map(q => q.q);

  const empMap = {};
  reviews.forEach(r => {
    const name = r.employee_name || r.employee || "";
    const code = r.employee_code || r.empId || "";
    const q = r.cycle_name || r.quarter || r.q || "";
    const hike = parseFloat(r.hikeGiven || r.approved_hike || 0);
    if (!name || !q) return;
    if (!empMap[code || name]) empMap[code || name] = { name, code, hikes: {} };
    if (hike > 0) empMap[code || name].hikes[q] = hike;
  });
  const empHikeData = Object.values(empMap);

  const clientMap = {};
  reviews.forEach(r => {
    const client = r.client_name || r.client || "";
    const q = r.cycle_name || r.quarter || r.q || "";
    if (!client || !q) return;
    if (!clientMap[client]) clientMap[client] = { client, counts: {}, totalEmps: 0 };
    if (!clientMap[client].counts[q]) clientMap[client].counts[q] = 0;
    if (r.status === "Submitted" || r.status === "Approved") clientMap[client].counts[q]++;
  });
  (clients || []).forEach(cl => { const name = cl.name || ""; if (clientMap[name]) clientMap[name].totalEmps = (cl.stakeholders || []).length; });
  const clientData = Object.values(clientMap);

  const allNames = ["All", ...empHikeData.map(e => e.name)];
  const allClientNames = ["All", ...clientData.map(c => c.client)];

  const q1Label = cmpQ1 || allQuarters[0] || "";
  const q2Label = cmpQ2 || allQuarters[allQuarters.length - 1] || "";
  const getQData = (qLabel) => qData.find(q => q.q === qLabel) || {};

  const totalReviews = reviews.length;
  const approvedReviews = reviews.filter(r => r.status === "Approved").length;
  const allHikes = reviews.map(r => parseFloat(r.hikeGiven || r.approved_hike || 0)).filter(v => v > 0);
  const highestAvgHike = allHikes.length ? Math.max(...allHikes).toFixed(1) + "%" : "---";
  const activeCycle = (cycles || []).find(c => c.status === "Active");
  const activeLabel = activeCycle ? (activeCycle.q + " | " + (activeCycle.sent ? Math.round(((activeCycle.submitted || 0) / activeCycle.sent) * 100) : 0) + "%") : "No active cycle";

  const dateStr = todayIST();
  const getFilteredEmpData = () => empHikeData.filter(e => empFilter === "All" || e.name === empFilter);
  const getFilteredClientData = () => clientData.filter(c => clientFilter === "All" || c.client === clientFilter);

  // ── Tab 0 exports ────────────────────────────────────────────────────────────
  const exportQuarterOverviewXLS = async () => {
    if (!qData.length) { showToast("No quarter data to export", "error"); return; }
    showToast("Generating Excel...");
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook(); wb.creator = "Dolluz Corp EPR";
      const ws = wb.addWorksheet("Quarter Overview", { views: [{ state: "frozen", ySplit: 1 }] });
      ws.columns = [{ key: "q", width: 16 }, { key: "sent", width: 16 }, { key: "submitted", width: 16 }, { key: "approved", width: 16 }, { key: "avgHike", width: 14 }, { key: "completion", width: 15 }];
      applyXlsHeader(ws, ["Quarter", "Reviews Sent", "Submitted", "Approved", "Avg Hike %", "Completion %"]);
      qData.forEach((q, idx) => {
        const bg = idx % 2 === 0 ? "FFFFFFFF" : "FFFAFAFA";
        applyXlsDataRow(ws, idx + 2, [q.q, q.sent, q.submitted, q.approved, q.avgHike ? q.avgHike + "%" : "0%", q.completion + "%"], bg);
        if (activeCycle && q.q === activeCycle.q) {
          const row = ws.getRow(idx + 2);
          for (let i = 1; i <= 6; i++) {
            row.getCell(i).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF5F0" } };
            row.getCell(i).font = { size: 9, color: { argb: "FFB45309" }, bold: true };
          }
        }
      });
      const buf = await wb.xlsx.writeBuffer();
      triggerDownload(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Quarter_Overview_" + dateStr + ".xlsx");
      showToast("Quarter overview exported", "#10B981");
    } catch (err) { console.error(err); showToast("Export failed", "error"); }
  };

  const exportQuarterOverviewPDF = async () => {
    if (!qData.length) { showToast("No quarter data to export", "error"); return; }
    showToast("Generating PDF...");
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      pdfHeader(doc, "Quarter Overview", qData.length + " quarter(s)", true);
      doc.autoTable({
        startY: 22,
        head: [["Quarter", "Sent", "Submitted", "Approved", "Avg Hike %", "Completion %"]],
        body: qData.map(q => [q.q, q.sent, q.submitted, q.approved, q.avgHike ? q.avgHike + "%" : "0%", q.completion + "%"]),
        headStyles: { fillColor: [232, 82, 10], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
        bodyStyles: { fontSize: 9 }, alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 10, right: 10 },
      });
      pdfFooter(doc, true);
      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save("Quarter_Overview_" + dateStr + ".pdf");
      showToast("Quarter overview PDF downloaded", "#10B981");
    } catch (err) { console.error(err); showToast("PDF failed", "error"); }
  };

  // ── Tab 1 exports ────────────────────────────────────────────────────────────
  const exportEmployeeDrilldownXLS = async () => {
    const rows = getFilteredEmpData();
    if (!rows.length) { showToast("No data to export", "error"); return; }
    showToast("Generating Excel...");
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook(); wb.creator = "Dolluz Corp EPR";
      const ws = wb.addWorksheet("Employee Drilldown", { views: [{ state: "frozen", ySplit: 1 }] });
      const headers = ["Employee ID", "Name"].concat(allQuarters).concat(["Avg Hike %", "Trend"]);
      ws.columns = [{ key: "code", width: 14 }, { key: "name", width: 22 }].concat(allQuarters.map(q => ({ key: q, width: 13 }))).concat([{ key: "avg", width: 14 }, { key: "trend", width: 12 }]);
      applyXlsHeader(ws, headers);
      rows.forEach((e, idx) => {
        const vals = allQuarters.map(q => e.hikes[q] || null).filter(v => v !== null);
        const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
        const delta = vals.length >= 2 ? (vals[vals.length - 1] - vals[0]).toFixed(1) : null;
        const bg = idx % 2 === 0 ? "FFFFFFFF" : "FFFAFAFA";
        const rowValues = [e.code || "", e.name].concat(allQuarters.map(q => e.hikes[q] ? e.hikes[q] + "%" : "---")).concat([avg ? avg + "%" : "---", delta !== null ? (parseFloat(delta) >= 0 ? "Up " + delta : "Down " + Math.abs(delta)) : "---"]);
        applyXlsDataRow(ws, idx + 2, rowValues, bg);
      });
      const buf = await wb.xlsx.writeBuffer();
      triggerDownload(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Employee_Drilldown_" + dateStr + ".xlsx");
      showToast("Employee drill-down exported", "#10B981");
    } catch (err) { console.error(err); showToast("Export failed", "error"); }
  };

  const exportEmployeeDrilldownPDF = async () => {
    const rows = getFilteredEmpData();
    if (!rows.length) { showToast("No data to export", "error"); return; }
    showToast("Generating PDF...");
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      pdfHeader(doc, "Employee Hike Drill-down", rows.length + " employee(s)  |  Filter: " + empFilter, true);
      const headers = [["Employee ID", "Name"].concat(allQuarters).concat(["Avg %", "Trend"])];
      const body = rows.map(e => {
        const vals = allQuarters.map(q => e.hikes[q] || null).filter(v => v !== null);
        const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
        const delta = vals.length >= 2 ? (vals[vals.length - 1] - vals[0]).toFixed(1) : null;
        return [e.code || "", e.name].concat(allQuarters.map(q => e.hikes[q] ? e.hikes[q] + "%" : "---")).concat([avg ? avg + "%" : "---", delta !== null ? (parseFloat(delta) >= 0 ? "+" + delta : delta) : "---"]);
      });
      const quarterColW = Math.min(20, Math.floor(200 / Math.max(allQuarters.length, 1)));
      const colStyles = { 0: { cellWidth: 18, fontStyle: "bold" }, 1: { cellWidth: 28, fontStyle: "bold" } };
      allQuarters.forEach((_, i) => { colStyles[i + 2] = { cellWidth: quarterColW, halign: "center" }; });
      colStyles[allQuarters.length + 2] = { cellWidth: 16, halign: "center" };
      colStyles[allQuarters.length + 3] = { cellWidth: 16, halign: "center" };
      doc.autoTable({ startY: 22, head: headers, body, headStyles: { fillColor: [232, 82, 10], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 }, bodyStyles: { fontSize: 8 }, alternateRowStyles: { fillColor: [248, 250, 252] }, columnStyles: colStyles, margin: { left: 10, right: 10 } });
      pdfFooter(doc, true);
      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save("Employee_Drilldown_" + dateStr + ".pdf");
      showToast("Employee drill-down PDF downloaded", "#10B981");
    } catch (err) { console.error(err); showToast("PDF failed", "error"); }
  };

  // ── Tab 2 exports ────────────────────────────────────────────────────────────
  const exportClientPerfXLS = async () => {
    const rows = getFilteredClientData();
    if (!rows.length) { showToast("No data to export", "error"); return; }
    showToast("Generating Excel...");
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook(); wb.creator = "Dolluz Corp EPR";
      const ws = wb.addWorksheet("Client Performance", { views: [{ state: "frozen", ySplit: 1 }] });
      ws.columns = [{ key: "client", width: 26 }].concat(allQuarters.map(q => ({ key: q, width: 14 }))).concat([{ key: "total", width: 18 }]);
      applyXlsHeader(ws, ["Client"].concat(allQuarters).concat(["Total Submissions"]));
      rows.forEach((c, idx) => {
        const total = Object.values(c.counts).reduce((a, b) => a + b, 0);
        const bg = idx % 2 === 0 ? "FFFFFFFF" : "FFFAFAFA";
        applyXlsDataRow(ws, idx + 2, [c.client].concat(allQuarters.map(q => c.counts[q] || 0)).concat([total]), bg);
      });
      const buf = await wb.xlsx.writeBuffer();
      triggerDownload(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Client_Performance_" + dateStr + ".xlsx");
      showToast("Client performance exported", "#10B981");
    } catch (err) { console.error(err); showToast("Export failed", "error"); }
  };

  const exportClientPerfPDF = async () => {
    const rows = getFilteredClientData();
    if (!rows.length) { showToast("No data to export", "error"); return; }
    showToast("Generating PDF...");
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      pdfHeader(doc, "Client Performance", rows.length + " client(s)  |  Filter: " + clientFilter, true);
      const headers = [["Client"].concat(allQuarters).concat(["Total"])];
      const body = rows.map(c => { const total = Object.values(c.counts).reduce((a, b) => a + b, 0); return [c.client].concat(allQuarters.map(q => c.counts[q] || 0)).concat([total]); });
      const quarterColW = Math.min(22, Math.floor(200 / Math.max(allQuarters.length + 1, 1)));
      const colStyles = { 0: { cellWidth: 40, fontStyle: "bold" } };
      allQuarters.forEach((_, i) => { colStyles[i + 1] = { cellWidth: quarterColW, halign: "center" }; });
      colStyles[allQuarters.length + 1] = { cellWidth: 20, halign: "center", fontStyle: "bold" };
      doc.autoTable({ startY: 22, head: headers, body, headStyles: { fillColor: [232, 82, 10], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 }, bodyStyles: { fontSize: 9 }, alternateRowStyles: { fillColor: [248, 250, 252] }, columnStyles: colStyles, margin: { left: 10, right: 10 } });
      pdfFooter(doc, true);
      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save("Client_Performance_" + dateStr + ".pdf");
      showToast("Client performance PDF downloaded", "#10B981");
    } catch (err) { console.error(err); showToast("PDF failed", "error"); }
  };

  // ── Tab 3 exports ────────────────────────────────────────────────────────────
  const exportQtrComparisonXLS = async () => {
    showToast("Generating Excel...");
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook(); wb.creator = "Dolluz Corp EPR";
      const ws = wb.addWorksheet("Quarter Comparison", { views: [{ state: "frozen", ySplit: 1 }] });
      ws.columns = [{ key: "metric", width: 22 }, { key: "q1", width: 20 }, { key: "q2", width: 20 }, { key: "change", width: 14 }];
      applyXlsHeader(ws, ["Metric", q1Label, q2Label, "Change"]);
      const d1 = getQData(q1Label); const d2 = getQData(q2Label);
      const metrics = [["Reviews Sent", d1.sent, d2.sent], ["Submitted", d1.submitted, d2.submitted], ["Approved", d1.approved, d2.approved], ["Avg Hike %", d1.avgHike ? d1.avgHike + "%" : "0%", d2.avgHike ? d2.avgHike + "%" : "0%"], ["Completion %", (d1.completion || 0) + "%", (d2.completion || 0) + "%"]];
      metrics.forEach(([metric, v1, v2], idx) => {
        const n1 = parseFloat(String(v1)) || 0; const n2 = parseFloat(String(v2)) || 0;
        const diff = (n2 - n1).toFixed(1);
        const bg = idx % 2 === 0 ? "FFFFFFFF" : "FFFAFAFA";
        applyXlsDataRow(ws, idx + 2, [metric, v1 != null ? v1 : "---", v2 != null ? v2 : "---", parseFloat(diff) > 0 ? "+" + diff : diff], bg);
        const changeCell = ws.getRow(idx + 2).getCell(4);
        const dn = parseFloat(diff);
        changeCell.font = { size: 9, bold: true, color: { argb: dn > 0 ? "FF10B981" : dn < 0 ? "FFEF4444" : "FF94A3B8" } };
      });
      const buf = await wb.xlsx.writeBuffer();
      triggerDownload(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Quarter_Comparison_" + q1Label + "_vs_" + q2Label + "_" + dateStr + ".xlsx");
      showToast("Quarter comparison exported", "#10B981");
    } catch (err) { console.error(err); showToast("Export failed", "error"); }
  };

  const exportQtrComparisonPDF = async () => {
    showToast("Generating PDF...");
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      pdfHeader(doc, "Quarter Comparison", q1Label + " vs " + q2Label, false);
      const d1 = getQData(q1Label); const d2 = getQData(q2Label);
      const body = [
        ["Reviews Sent", d1.sent != null ? d1.sent : "---", d2.sent != null ? d2.sent : "---"],
        ["Submitted", d1.submitted != null ? d1.submitted : "---", d2.submitted != null ? d2.submitted : "---"],
        ["Approved", d1.approved != null ? d1.approved : "---", d2.approved != null ? d2.approved : "---"],
        ["Avg Hike %", d1.avgHike ? d1.avgHike + "%" : "0%", d2.avgHike ? d2.avgHike + "%" : "0%"],
        ["Completion %", (d1.completion || 0) + "%", (d2.completion || 0) + "%"],
      ].map(([metric, v1, v2]) => {
        const n1 = parseFloat(String(v1)) || 0; const n2 = parseFloat(String(v2)) || 0;
        const diff = (n2 - n1).toFixed(1);
        return [metric, v1, v2, parseFloat(diff) > 0 ? "+" + diff : diff];
      });
      doc.autoTable({
        startY: 22,
        head: [["Metric", q1Label, q2Label, "Change"]],
        body,
        headStyles: { fillColor: [232, 82, 10], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 10 },
        bodyStyles: { fontSize: 10 }, alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { cellWidth: 50, fontStyle: "bold" }, 1: { halign: "center" }, 2: { halign: "center" }, 3: { halign: "center", fontStyle: "bold" } },
        margin: { left: 20, right: 20 },
      });
      pdfFooter(doc, false);
      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save("Quarter_Comparison_" + q1Label + "_vs_" + q2Label + "_" + dateStr + ".pdf");
      showToast("Quarter comparison PDF downloaded", "#10B981");
    } catch (err) { console.error(err); showToast("PDF failed", "error"); }
  };

  // ── Tab 4 exports ────────────────────────────────────────────────────────────
  const exportSubmissionHistoryXLS = async () => {
    if (!reviews.length) { showToast("No submission data to export", "error"); return; }
    showToast("Generating Excel...");
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook(); wb.creator = "Dolluz Corp EPR";
      const ws = wb.addWorksheet("Submission History", { views: [{ state: "frozen", ySplit: 1 }] });
      ws.columns = [{ key: "emp", width: 22 }, { key: "code", width: 14 }, { key: "client", width: 22 }, { key: "quarter", width: 14 }, { key: "stakeholder", width: 22 }, { key: "status", width: 16 }, { key: "submitted", width: 22 }, { key: "hike", width: 14 }];
      applyXlsHeader(ws, ["Employee", "Employee ID", "Client", "Quarter", "Stakeholder", "Status", "Submitted At", "Hike Given"]);
      const STATUS_COLORS = { "Approved": { bg: "FFD1FAE5", fg: "FF065F46" }, "Submitted": { bg: "FFDBEAFE", fg: "FF1E40AF" }, "In Progress": { bg: "FFFEF3C7", fg: "FF92400E" }, "Not Started": { bg: "FFF1F5F9", fg: "FF64748B" } };
      const revList = reviews.slice().reverse();
      revList.forEach((r, idx) => {
        const bg = idx % 2 === 0 ? "FFFFFFFF" : "FFFAFAFA";
        const status = r.status || "";
        applyXlsDataRow(ws, idx + 2, [r.employee_name || r.employee || "", r.employee_code || r.empId || "", r.client_name || r.client || "", r.cycle_name || r.quarter || "", r.stakeholder_name || r.stakeholder || "", status, r.submittedAt || "---", r.hikeGiven ? r.hikeGiven + "%" : "---"], bg);
        const sc = STATUS_COLORS[status];
        if (sc) {
          const cell = ws.getRow(idx + 2).getCell(6);
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: sc.bg } };
          cell.font = { size: 9, bold: true, color: { argb: sc.fg } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        }
      });
      const buf = await wb.xlsx.writeBuffer();
      triggerDownload(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Submission_History_" + dateStr + ".xlsx");
      showToast("Submission history exported -- " + reviews.length + " review(s)", "#10B981");
    } catch (err) { console.error(err); showToast("Export failed", "error"); }
  };

  const exportSubmissionHistoryPDF = async () => {
    if (!reviews.length) { showToast("No submission data to export", "error"); return; }
    showToast("Generating PDF...");
    try {
      const JsPDF = await loadJsPDF();
      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      pdfHeader(doc, "Submission History", reviews.length + " review(s)  |  " + allQuarters.length + " quarter(s)", true);
      const statusBg = s => ({ "Approved": [209, 250, 229], "Submitted": [219, 234, 254], "In Progress": [254, 243, 199], "Not Started": [241, 245, 249] }[s] || [241, 245, 249]);
      const statusFg = s => ({ "Approved": [16, 185, 129], "Submitted": [59, 130, 246], "In Progress": [245, 158, 11], "Not Started": [148, 163, 184] }[s] || [148, 163, 184]);
      doc.autoTable({
        startY: 22,
        head: [["Employee", "Employee ID", "Client", "Quarter", "Stakeholder", "Status", "Submitted", "Hike"]],
        body: reviews.slice().reverse().map(r => [r.employee_name || r.employee || "", r.employee_code || r.empId || "", r.client_name || r.client || "", r.cycle_name || r.quarter || "", r.stakeholder_name || r.stakeholder || "", r.status || "", r.submittedAt || "---", r.hikeGiven ? r.hikeGiven + "%" : "---"]),
        headStyles: { fillColor: [232, 82, 10], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
        bodyStyles: { fontSize: 8 }, alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { cellWidth: 36, fontStyle: "bold" }, 1: { cellWidth: 20 }, 2: { cellWidth: 30 }, 3: { cellWidth: 20, halign: "center" }, 4: { cellWidth: 36 }, 5: { cellWidth: 24, halign: "center" }, 6: { cellWidth: 30 }, 7: { cellWidth: 16, halign: "center" } },
        didDrawCell: (data) => {
          if (data.section === "body" && data.column.index === 5) {
            const status = data.cell.raw;
            const bg = statusBg(status); const fg = statusFg(status);
            const { x, y, width: w, height: h } = data.cell;
            doc.setFillColor(bg[0], bg[1], bg[2]);
            doc.roundedRect(x + 1, y + 1.5, w - 2, h - 3, 1.5, 1.5, "F");
            doc.setTextColor(fg[0], fg[1], fg[2]);
            doc.setFontSize(7); doc.setFont("helvetica", "bold");
            doc.text(status, x + w / 2, y + h / 2 + 0.5, { align: "center", baseline: "middle" });
          }
        },
        margin: { left: 10, right: 10 },
      });
      pdfFooter(doc, true);
      applyPdfWatermark(doc, await loadWatermarkDataUrl());
      doc.save("Submission_History_" + dateStr + ".pdf");
      showToast("Submission history PDF downloaded", "#10B981");
    } catch (err) { console.error(err); showToast("PDF failed", "error"); }
  };

  // ── Render helpers ────────────────────────────────────────────────────────────
  const REPORT_TABS = ["Quarter Overview", "Employee Drill-down", "Client Performance", "Quarter Comparison", "Submission History"];
  const Tab = ({ i, label }) => (
    <button onClick={() => setActiveTab(i)} style={{ padding: "10px 16px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: activeTab === i ? 700 : 500, background: activeTab === i ? "#FFF5F0" : "#fff", color: activeTab === i ? "#E8520A" : "#475569", borderBottom: activeTab === i ? "2.5px solid #E8520A" : "2.5px solid transparent", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );
  const ExportBar = ({ onPDF, onXLS }) => (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
      <button className="btn-secondary" style={{ fontSize: 12, color: "#10B981", borderColor: "#A7F3D0" }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.background = "#F0FDF4"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "#A7F3D0"; e.currentTarget.style.background = "#fff"; }}
        onClick={onXLS}>📊 Excel Export</button>
      <button className="btn-secondary" style={{ fontSize: 12, color: "#EF4444", borderColor: "#FECACA" }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.background = "#fff"; }}
        onClick={onPDF}>📄 PDF Export</button>
    </div>
  );

  return (
    <div className="fade-in">
      <TopBar title="Reports & Analytics" subtitle="Multi-dimensional EPR performance insights and exports" {...topBarProps} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 18 }}>
        {[{ l: "Total Reviews (All Time)", v: totalReviews, c: "#0D1B2A", bg: "#F8FAFC" }, { l: "Approved Reviews", v: approvedReviews, c: "#10B981", bg: "#F0FDF4" }, { l: "Highest Hike Given", v: highestAvgHike, c: "#E8520A", bg: "#FFF5F0" }, { l: "Active Quarter", v: activeLabel, c: "#3B82F6", bg: "#EFF6FF" }].map(s => (
          <div key={s.l} style={{ background: s.bg, borderRadius: 10, padding: "14px 18px", border: "1px solid #F1F5F9" }}>
            <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{s.l}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 20, fontWeight: 800, color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 0 }}>
        <div style={{ display: "flex", borderBottom: "1px solid #F1F5F9", overflowX: "auto" }}>
          {REPORT_TABS.map((t, i) => <Tab key={i} i={i} label={t} />)}
        </div>

        {activeTab === 0 && (
          <div style={{ padding: "20px 24px" }}>
            {qData.length === 0 ? <div style={{ textAlign: "center", padding: "48px 24px", color: "#94A3B8", fontSize: 13 }}>No cycle data available.</div> : (
              <>
                <ExportBar onPDF={exportQuarterOverviewPDF} onXLS={exportQuarterOverviewXLS} />
                <div style={{ display: "flex", gap: 12, marginBottom: 24, overflowX: "auto", paddingBottom: 4 }}>
                  {qData.map((q, i) => (
                    <div key={i} style={{ flex: "0 0 auto", minWidth: 160, background: "#F8FAFC", borderRadius: 10, padding: "16px", border: activeCycle && q.q === activeCycle.q ? "2px solid rgba(232,82,10,.3)" : "1px solid #F1F5F9" }}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 13, color: activeCycle && q.q === activeCycle.q ? "#E8520A" : "#64748B", marginBottom: 10 }}>{q.q}</div>
                      {[["Sent", q.sent, "#475569"], ["Submitted", q.submitted, "#3B82F6"], ["Approved", q.approved, "#10B981"], ["Avg Hike", q.avgHike ? q.avgHike + "%" : "---", "#E8520A"]].map(([l, v, c]) => (
                        <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}>
                          <span style={{ color: "#94A3B8" }}>{l}</span><span style={{ fontWeight: 700, color: c }}>{v}</span>
                        </div>
                      ))}
                      <div className="progress-bar" style={{ marginTop: 6 }}><div className="progress-fill" style={{ width: q.completion + "%", background: activeCycle && q.q === activeCycle.q ? "#E8520A" : "#10B981" }} /></div>
                      <div style={{ fontSize: 10, color: "#94A3B8", textAlign: "right", marginTop: 3 }}>{q.completion}% complete</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 16 }}>Hike % Trend - All Quarters</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={qData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0D1B2A", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                    <Line type="monotone" dataKey="avgHike" name="Avg Hike %" stroke="#E8520A" strokeWidth={3} dot={{ fill: "#E8520A", r: 5, strokeWidth: 0 }} activeDot={{ r: 7 }} />
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
        )}

        {activeTab === 1 && (
          <div style={{ padding: "20px 24px" }}>
            {empHikeData.length === 0 ? <div style={{ textAlign: "center", padding: "48px 24px", color: "#94A3B8", fontSize: 13 }}>No employee review data available.</div> : (
              <>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
                  <select className="inp" value={empFilter} onChange={e => setEmpFilter(e.target.value)} style={{ maxWidth: 220 }}>
                    {allNames.map(n => <option key={n}>{n}</option>)}
                  </select>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <button className="btn-ghost" style={{ fontSize: 12, color: "#10B981" }} onClick={exportEmployeeDrilldownXLS}>📊 Excel</button>
                    <button className="btn-ghost" style={{ fontSize: 12, color: "#EF4444" }} onClick={exportEmployeeDrilldownPDF}>📄 PDF</button>
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ minWidth: 600 }}>
                    <thead><tr><th>Employee</th>{allQuarters.map(q => <th key={q} style={{ textAlign: "center", fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{q}</th>)}<th style={{ textAlign: "center" }}>Avg Hike</th><th style={{ textAlign: "center" }}>Trend</th></tr></thead>
                    <tbody>
                      {getFilteredEmpData().map((e, idx) => {
                        const vals = allQuarters.map(q => e.hikes[q] || null).filter(v => v !== null);
                        const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : "---";
                        const delta = vals.length >= 2 ? (vals[vals.length - 1] - vals[0]).toFixed(1) : null;
                        return (
                          <tr key={e.code || idx}>
                            <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar name={e.name} size={28} /><div><div style={{ fontWeight: 600, fontSize: 12 }}>{e.name}</div><div style={{ fontSize: 10, color: "#94A3B8" }}>{e.code}</div></div></div></td>
                            {allQuarters.map((q, i) => { const v = e.hikes[q] || null; return <td key={i} style={{ textAlign: "center" }}>{v !== null ? <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 12, color: v >= 20 ? "#10B981" : v >= 17 ? "#E8520A" : "#475569" }}>{v}%</span> : <span style={{ color: "#CBD5E1", fontSize: 11 }}>---</span>}</td>; })}
                            <td style={{ textAlign: "center", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 12, color: "#3B82F6" }}>{avg}{avg !== "---" ? "%" : ""}</td>
                            <td style={{ textAlign: "center" }}>{delta !== null ? <span style={{ fontWeight: 700, fontSize: 12, color: parseFloat(delta) >= 0 ? "#10B981" : "#EF4444" }}>{parseFloat(delta) >= 0 ? "Up" : "Down"} {Math.abs(parseFloat(delta))}</span> : <span style={{ color: "#94A3B8" }}>---</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 2 && (
          <div style={{ padding: "20px 24px" }}>
            {clientData.length === 0 ? <div style={{ textAlign: "center", padding: "48px 24px", color: "#94A3B8", fontSize: 13 }}>No client review data available.</div> : (
              <>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
                  <select className="inp" value={clientFilter} onChange={e => setClientFilter(e.target.value)} style={{ maxWidth: 260 }}>
                    {allClientNames.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <button className="btn-ghost" style={{ fontSize: 12, color: "#10B981" }} onClick={exportClientPerfXLS}>📊 Excel</button>
                    <button className="btn-ghost" style={{ fontSize: 12, color: "#EF4444" }} onClick={exportClientPerfPDF}>📄 PDF</button>
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ minWidth: 600 }}>
                    <thead><tr><th>Client</th>{allQuarters.map(q => <th key={q} style={{ textAlign: "center", fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>{q}</th>)}<th style={{ textAlign: "center" }}>Total</th></tr></thead>
                    <tbody>
                      {getFilteredClientData().map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 600, fontSize: 13 }}>{c.client}</td>
                          {allQuarters.map((q, j) => { const v = c.counts[q] || 0; return <td key={j} style={{ textAlign: "center" }}><span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 12, color: v >= 4 ? "#10B981" : v >= 2 ? "#E8520A" : v === 0 ? "#CBD5E1" : "#475569" }}>{v}</span></td>; })}
                          <td style={{ textAlign: "center", fontWeight: 700, color: "#3B82F6" }}>{Object.values(c.counts).reduce((a, b) => a + b, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 3 && (
          <div style={{ padding: "20px 24px" }}>
            {allQuarters.length < 2 ? <div style={{ textAlign: "center", padding: "48px 24px", color: "#94A3B8", fontSize: 13 }}>Need at least 2 quarters of data to compare.</div> : (
              <>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Compare:</span>
                    <select className="inp" value={q1Label} onChange={e => setCmpQ1(e.target.value)} style={{ maxWidth: 140 }}>{allQuarters.map(q => <option key={q}>{q}</option>)}</select>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#94A3B8" }}>vs</span>
                    <select className="inp" value={q2Label} onChange={e => setCmpQ2(e.target.value)} style={{ maxWidth: 140 }}>{allQuarters.map(q => <option key={q}>{q}</option>)}</select>
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <button className="btn-ghost" style={{ fontSize: 12, color: "#10B981" }} onClick={exportQtrComparisonXLS}>📊 Excel</button>
                    <button className="btn-ghost" style={{ fontSize: 12, color: "#EF4444" }} onClick={exportQtrComparisonPDF}>📄 PDF</button>
                  </div>
                </div>
                {(() => {
                  const d1 = getQData(q1Label); const d2 = getQData(q2Label);
                  const metrics = [{ l: "Reviews Sent", v1: d1.sent, v2: d2.sent }, { l: "Submitted", v1: d1.submitted, v2: d2.submitted }, { l: "Approved", v1: d1.approved, v2: d2.approved }, { l: "Avg Hike %", v1: d1.avgHike, v2: d2.avgHike }, { l: "Completion %", v1: d1.completion, v2: d2.completion }];
                  return (
                    <div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                        {[{ q: q1Label, d: d1, color: "#E8520A" }, { q: q2Label, d: d2, color: "#3B82F6" }].map(({ q, d, color }) => (
                          <div key={q} style={{ background: "#F8FAFC", borderRadius: 12, padding: "18px", border: "2px solid " + color + "22" }}>
                            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color, marginBottom: 12 }}>{q}</div>
                            {[["Sent", d.sent || 0], ["Submitted", d.submitted || 0], ["Approved", d.approved || 0], ["Avg Hike", (d.avgHike || 0) + "%"], ["Completion", (d.completion || 0) + "%"]].map(([l, v]) => (
                              <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                                <span style={{ color: "#64748B" }}>{l}</span><span style={{ fontWeight: 700, color: "#0D1B2A" }}>{v}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                      <table>
                        <thead><tr><th>Metric</th><th style={{ textAlign: "center", color: "#E8520A" }}>{q1Label}</th><th style={{ textAlign: "center", color: "#3B82F6" }}>{q2Label}</th><th style={{ textAlign: "center" }}>Change</th></tr></thead>
                        <tbody>
                          {metrics.map(m => { const diff = (m.v2 || 0) - (m.v1 || 0); return (<tr key={m.l}><td style={{ fontWeight: 500, fontSize: 13 }}>{m.l}</td><td style={{ textAlign: "center", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>{m.v1 != null ? m.v1 : "---"}</td><td style={{ textAlign: "center", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>{m.v2 != null ? m.v2 : "---"}</td><td style={{ textAlign: "center" }}><span style={{ fontWeight: 700, fontSize: 12, color: diff > 0 ? "#10B981" : diff < 0 ? "#EF4444" : "#94A3B8" }}>{diff > 0 ? "Up" : diff < 0 ? "Down" : "="} {Math.abs(diff)}</span></td></tr>); })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {activeTab === 4 && (
          <div style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>All {reviews.length} review record{reviews.length !== 1 ? "s" : ""} across {allQuarters.length} quarter{allQuarters.length !== 1 ? "s" : ""}</span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button className="btn-secondary" style={{ fontSize: 12, color: "#10B981", borderColor: "#A7F3D0" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.background = "#F0FDF4"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#A7F3D0"; e.currentTarget.style.background = "#fff"; }}
                  onClick={exportSubmissionHistoryXLS}>📊 Excel Export</button>
                <button className="btn-secondary" style={{ fontSize: 12, color: "#EF4444", borderColor: "#FECACA" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.background = "#fff"; }}
                  onClick={exportSubmissionHistoryPDF}>📄 PDF Export</button>
              </div>
            </div>
            {reviews.length === 0 ? <div style={{ textAlign: "center", padding: "32px 24px", color: "#94A3B8", fontSize: 13 }}>No review records found.</div> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ minWidth: 860 }}>
                  <thead><tr><th>Employee</th><th>Client</th><th>Quarter</th><th>Stakeholder</th><th>Status</th><th>Submitted</th><th>Hike Given</th></tr></thead>
                  <tbody>
                    {reviews.slice().reverse().map((r, i) => (
                      <tr key={r.id || i}>
                        <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar name={r.employee_name || r.employee || "?"} size={26} /><div><div style={{ fontWeight: 600, fontSize: 12 }}>{r.employee_name || r.employee}</div><div style={{ fontSize: 10, color: "#94A3B8" }}>{r.employee_code || r.empId}</div></div></div></td>
                        <td style={{ fontSize: 12, color: "#475569" }}>{(r.client_name || r.client || "").split(" ")[0]}</td>
                        <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#475569" }}>{r.cycle_name || r.quarter}</td>
                        <td style={{ fontSize: 12, color: "#64748B" }}>{r.stakeholder_name || r.stakeholder}</td>
                        <td><Badge status={r.status} /></td>
                        <td style={{ fontSize: 12, color: "#475569", fontFamily: "'JetBrains Mono',monospace" }}>{r.submittedAt || "---"}</td>
                        <td style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 12, color: r.hikeGiven ? "#10B981" : "#CBD5E1" }}>{r.hikeGiven ? r.hikeGiven + "%" : "---"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {toast && <Toast msg={toast} type={toastType} />}
    </div>
  );
};

export default Reports;